import { Box, CircularProgress, styled, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useLocation, NavLink } from "react-router-dom";

import Logo from "assets/images/newLogo.png";

import { resetPassword } from "@/services/auth.service";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { CustomButton } from "@/components/custom/custom-button";
import { resetPasswordSchema } from "@/schemas/auth.schema";
import { showToast } from "@/redux/actions/toast-actions";
import { useDispatch } from "react-redux";

const PageRoot = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 440,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(1),
}));

const ResetPassword = () => {
    const dispatch = useDispatch();
  

  const navigate = useNavigate();
  const location = useLocation();

  const emailFromState = location.state?.email || "";

  const mutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: (data) => {
      dispatch(showToast(data?.message ?? "Email sent", "success"));
      navigate("/session/login", {
        replace: true,
      });
    },
  });

  const formik = useFormik({
    initialValues: {
      otp: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: resetPasswordSchema,
    onSubmit: (values) => {
      const payload = {
        otp: values.otp,
        phoneOrEmail: emailFromState,
        newPassword: values.password,
      };

      mutation.mutate(payload);
    },
  });

  return (
    <PageRoot>
      <Card>
        <Header>
          <NavLink to="/">
            <img src={Logo} alt="logo" style={{ width: 120 }} />
          </NavLink>
        </Header>

        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>
            Reset Password
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Enter your new password below to complete your password reset.
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            <FormTextInput
              formik={formik}
              name="otp"
              label="OTP Code"
              placeholder="Enter 6-digit code"
              isDisabled={mutation.isPending}
            />

            <FormTextInput
              formik={formik}
              name="password"
              label="New Password"
              type="password"
              isDisabled={mutation.isPending}
            />

            <FormTextInput
              formik={formik}
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              isDisabled={mutation.isPending}
            />

            {mutation.isError && (
              <Typography variant="body2" color="error">
                {mutation.error?.message || "Something went wrong. Try again."}
              </Typography>
            )}

            <CustomButton
              type="submit"
              disabled={!formik.isValid || !formik.dirty || mutation.isPending}
              sx={{ height: 44, mt: 1 }}
            >
              {mutation.isPending ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress
                    size={16}
                    thickness={5}
                    sx={{ color: "#fff" }}
                  />
                  Resetting...
                </Box>
              ) : (
                "Reset Password"
              )}
            </CustomButton>

            <Typography
              variant="body2"
              textAlign="center"
              color="text.secondary"
            >
              Remember your password?{" "}
              <NavLink
                to="/session/login"
                style={{
                  color: "inherit",
                  fontWeight: 600,
                }}
              >
                Sign in
              </NavLink>
            </Typography>
          </Box>
        </form>
      </Card>
    </PageRoot>
  );
};

export default ResetPassword;
